/**
 * **Kind of visit** (visits contract §4.5 and §4.5.1, Boards 8 and 21).
 *
 * One menu for every place a person picks a visit's kind — the quote's visit
 * popover, the package builder's and the phone sheet — and it lists **only the
 * kinds the company has switched on**, in the fixed order, `Work` first. A
 * visit whose kind has since been switched off keeps it: its own kind is
 * listed first, its help line replaced by `Off in Settings`, and picking
 * another is allowed while picking the off one again is not.
 *
 * `useEnabledVisitKinds()` is the client's one reader of the setting
 * (`GET /settings/visit-kinds/enabled`), fetched once per page load and
 * dropped by `forgetEnabledVisitKinds()` when Settings saves a new set.
 */
import { useEffect, useState, type ReactNode } from 'react'
import { VISIT_ROLES, type VisitRole } from '@rowted/shared'
import { MenuSelect, type MenuOption } from '../../components/menu-select.js'
import { request } from '../../lib/api.js'
import {
  IconCalendar, IconDropOff, IconLoop, IconPackage, IconPickUp, IconSearch, RECORD_ICONS,
} from '../../frame/icons.js'
import { KIND_OF_VISIT, OFF_IN_SETTINGS, VISIT_KIND_MENU } from '../copy.js'

let cached: Promise<VisitRole[]> | null = null

/** Drop the cached set — Settings › Product › Visit kinds calls it after a save. */
export function forgetEnabledVisitKinds(): void {
  cached = null
}

/** The company's enabled kinds; all six until the answer arrives, so no menu starts empty. */
export function useEnabledVisitKinds(): VisitRole[] {
  const [kinds, setKinds] = useState<VisitRole[]>([...VISIT_ROLES])
  useEffect(() => {
    let alive = true
    cached ??= request<{ kinds: VisitRole[] }>('/settings/visit-kinds/enabled')
      .then((body) => body.kinds)
      .catch(() => {
        cached = null
        return [...VISIT_ROLES]
      })
    void cached.then((found) => { if (alive) setKinds(found) })
    return () => { alive = false }
  }, [])
  return kinds
}

/** Board 8's glyph for each kind: labor · inspect · drop off · service · repeat · pick up. */
export function visitKindGlyph(role: VisitRole, size = 14): ReactNode {
  switch (role) {
    case 'work': {
      // The labor line's own glyph: a visit of Work is the crew doing the job.
      const Labor = RECORD_ICONS.labor
      return <Labor size={size} />
    }
    case 'inspect': return <IconSearch size={size} />
    case 'place': return <IconDropOff size={size} />
    case 'service': return <IconPackage size={size} />
    case 'swap': return <IconLoop size={size} />
    case 'pickup': return <IconPickUp size={size} />
    default: return <IconCalendar size={size} />
  }
}

/** The menu's rows: the visit's own kind first when it is off, then the enabled kinds in order. */
export function kindOptions(enabled: VisitRole[], current: VisitRole | null): MenuOption[] {
  const on = VISIT_ROLES.filter((role) => enabled.includes(role))
  const off = current && !on.includes(current) ? [current] : []
  return [...off, ...on].map((role) => ({
    value: role,
    label: VISIT_KIND_MENU[role].label,
    help: off.includes(role) ? OFF_IN_SETTINGS : VISIT_KIND_MENU[role].help,
    glyph: visitKindGlyph(role),
  }))
}

export function KindField({ value, onChange, current, id }: {
  value: VisitRole
  onChange: (role: VisitRole) => void
  /** The kind the visit had when it was opened — kept, and listed first, if it is off. */
  current: VisitRole | null
  id?: string
}) {
  const enabled = useEnabledVisitKinds()
  const options = kindOptions(enabled, current)
  return (
    <MenuSelect
      id={id}
      size="field"
      label={KIND_OF_VISIT}
      value={value}
      minWidth={300}
      options={options}
      sheet={{ title: KIND_OF_VISIT }}
      onChange={(next) => {
        // An off kind can be kept, never picked anew.
        if (!next || (next !== current && !enabled.includes(next as VisitRole))) return
        onChange(next as VisitRole)
      }}
    />
  )
}
